export const SIMPLE_EMAIL_TEMPLATE_MARKER = "[[WL-EMAIL]]";
const DEFAULT_SIMPLE_EMAIL_FOOTER_BLOCK = `
[Footer]
Title: WhaleLO - Luggage Storage & Delivery
Hotline: (+84) 866 888 431 (WhatsApp, Zalo)
Email: info@whalelo.net
Website: https://whalelo.net|whalelo.net
Note: This is an automated email. Please do not reply directly.
[/Footer]`;

export const isSimpleEmailTemplate = (content?: string) =>
	String(content || "")
		.trim()
		.startsWith(SIMPLE_EMAIL_TEMPLATE_MARKER);

export const ensureSimpleEmailFooterBlock = (content?: string) => {
	const normalizedContent = String(content || "");
	if (!isSimpleEmailTemplate(normalizedContent)) {
		return normalizedContent;
	}

	if (/\[footer\]/i.test(normalizedContent)) {
		return normalizedContent;
	}

	return `${normalizedContent.trimEnd()}\n${DEFAULT_SIMPLE_EMAIL_FOOTER_BLOCK}`;
};

const customerPaidTemplate = `${SIMPLE_EMAIL_TEMPLATE_MARKER}
Eyebrow: WhaleLO Booking
Title: Booking Confirmed
Subtitle: Your reservation has been successfully confirmed. Please review the booking details below.

Hello **\${customerName}**
Please keep this email ready when you arrive at the station.

[Summary]
Booking Number: \${orderId}
Customer Name: \${customerName}
Customer Phone: \${customerFullPhone}
Email: \${customerEmail}
Number of Luggages: \${quantity}
Amount!: \${total} USD
Drop-off Date: \${formattedTimeIn} (LOCAL TIME)
Pick-up Date: \${formattedTimeOut} (LOCAL TIME)
Storage Location: \${partnerName}
Address: \${partnerAddress}
Google Maps: \${mapLink}
[/Summary]

[Notes: Arrival Notes]
- Present this confirmation email when you arrive at the storage reception.
- For extra luggage or longer storage duration, create a new booking for the additional service.
[/Notes]

Thank you for choosing WhaleLO. If you need support, our team will be happy to help.
Best regards,
**WhaleLO Booking Team**
${DEFAULT_SIMPLE_EMAIL_FOOTER_BLOCK}`;

const adminPaidTemplate = `${SIMPLE_EMAIL_TEMPLATE_MARKER}
Eyebrow: WhaleLO Booking
Title: Booking Confirmed
Subtitle: A booking has been successfully confirmed. Please review the details below.

Hello team,
A concise booking summary is ready below.

[Summary]
Booking Number: \${orderId}
Customer Name: \${customerName}
Customer Phone: \${customerFullPhone}
Email: \${customerEmail}
Number of Luggages: \${quantity}
Amount!: \${total} USD
Drop-off Date: \${formattedTimeIn} (LOCAL TIME)
Pick-up Date: \${formattedTimeOut} (LOCAL TIME)
Storage Location: \${partnerName}
Address: \${partnerAddress}
Google Maps: \${mapLink}
[/Summary]

[Notes: Internal Notes]
- Review the booking details and follow up if manual support is needed.
[/Notes]
${DEFAULT_SIMPLE_EMAIL_FOOTER_BLOCK}`;

const partnerPaidTemplate = `${SIMPLE_EMAIL_TEMPLATE_MARKER}
Eyebrow: WhaleLO Booking
Title: New Booking Received
Subtitle: A new reservation has been created for your station. Please review the details below.

Hello **\${partnerName}**
A concise booking summary is ready below.

[Summary]
Booking Number: \${orderId}
Customer Name: \${customerName}
Number of Luggages: \${quantity}
Amount: \${total} USD
Drop-off Date: \${formattedTimeIn} (LOCAL TIME)
Pick-up Date: \${formattedTimeOut} (LOCAL TIME)
Storage Location: \${partnerName}
Address: \${partnerAddress}
[/Summary]

[Notes: Station Notes]
- Prepare the station to receive this booking.
[/Notes]
${DEFAULT_SIMPLE_EMAIL_FOOTER_BLOCK}`;

const customerCancelTemplate = `${SIMPLE_EMAIL_TEMPLATE_MARKER}
Eyebrow: WhaleLO Booking
Title: Booking Canceled
Subtitle: Your booking has been canceled. Please review the information below.

Hello **\${customerName}**
Your booking status has been updated.

[Summary]
Booking Number: \${orderId}
Customer Name: \${customerName}
Amount!: \${total} USD
Storage Location: \${partnerName}
Address: \${partnerAddress}
Google Maps: \${mapLink}
[/Summary]

[Notes: Support]
- If you need support, contact our team or create a new booking when ready.
[/Notes]
${DEFAULT_SIMPLE_EMAIL_FOOTER_BLOCK}`;

const adminCancelTemplate = `${SIMPLE_EMAIL_TEMPLATE_MARKER}
Eyebrow: WhaleLO Booking
Title: Booking Canceled
Subtitle: A booking has been canceled. Please review the information below.

Hello team,
The booking status has been updated.

[Summary]
Booking Number: \${orderId}
Customer Name: \${customerName}
Customer Phone: \${customerFullPhone}
Email: \${customerEmail}
Amount!: \${total} USD
Storage Location: \${partnerName}
Address: \${partnerAddress}
[/Summary]
${DEFAULT_SIMPLE_EMAIL_FOOTER_BLOCK}`;

const partnerCancelTemplate = `${SIMPLE_EMAIL_TEMPLATE_MARKER}
Eyebrow: WhaleLO Booking
Title: Booking Canceled
Subtitle: A booking for your station has been canceled.

Hello **\${partnerName}**
The booking status has been updated.

[Summary]
Booking Number: \${orderId}
Customer Name: \${customerName}
Number of Luggages: \${quantity}
Drop-off Date: \${formattedTimeIn} (LOCAL TIME)
Pick-up Date: \${formattedTimeOut} (LOCAL TIME)
[/Summary]
${DEFAULT_SIMPLE_EMAIL_FOOTER_BLOCK}`;

const paymentRequestConfirmationTemplate = `${SIMPLE_EMAIL_TEMPLATE_MARKER}
Eyebrow: WhaleLO Finance
Title: Payment Request Submitted Successfully
Subtitle: Your payout request has been received. Below is the request summary and the expected payout window (VN settlement calendar).

Hello **\${displayName}**
We have received your payment request for the current settlement cycle.

[Summary]
Station Name: \${stationName}
Display Name: \${displayName}
Requested Amount!: \${requestAmount} \${currencyCode}
Request submitted (date): \${requestDateUtc}
Request Time (UTC): \${requestTimeUtc}
Expected payout window: \${payoutWindow}
Settlement segment: \${payoutPeriodLabel}
[/Summary]

[Notes: Lịch thanh toán / Settlement schedule]
- Nếu station gửi request trong các ngày 1–15 của tháng: WhaleLO chuyển tiền trong khoảng ngày 1–5 của tháng tiếp theo (theo múi giờ Việt Nam).
- Nếu gửi từ ngày 16 đến hết tháng: chuyển tiền trong khoảng ngày 16–20 của tháng tiếp theo (theo múi giờ Việt Nam).
- Dòng "Expected payout window" phản ánh cửa sổ dự kiến cho yêu cầu này (đồng bộ với hệ thống).
- Our finance team will contact you via your registered station email if any clarification is needed.
[/Notes]
${DEFAULT_SIMPLE_EMAIL_FOOTER_BLOCK}`;

const paymentFinalizedTemplate = `${SIMPLE_EMAIL_TEMPLATE_MARKER}
Eyebrow: WhaleLO Finance
Title: Payment Finalized
Subtitle: The payable amount for this cycle is confirmed. Payout will follow the VN settlement window below.

Hello **\${displayName}**,
This email summarizes bookings/requests included in this payment cycle.

[Summary]
Station Name: \${stationName}
Display Name: \${displayName}
Cycle period (bookings/requests): \${fromDate} – \${toDate}
Request Count: \${requestCount}
Total Amount!: \${totalAmount} \${currencyCode}
Scheduled payout window: \${payoutWindow}
Schedule summary: \${paymentDate}
Settlement segment: \${payoutPeriodLabel}
[/Summary]

[Notes: Lịch thanh toán / Settlement schedule]
- Kỳ request ngày 1–15: thanh toán ngày 1–5 tháng sau; kỳ 16–cuối tháng: thanh toán ngày 16–20 tháng sau (giờ Việt Nam).
- "Scheduled payout window" là khoảng ngày chi tiết; "Schedule summary" là dạng tóm tắt cùng kỳ.
[/Notes]
${DEFAULT_SIMPLE_EMAIL_FOOTER_BLOCK}`;

const stationRequestConfirmationTemplate = `${SIMPLE_EMAIL_TEMPLATE_MARKER}
Eyebrow: WhaleLO Partnership
Title: Application Received
Subtitle: Thank you for your interest in partnering with WhaleLO.

Hello **${"${name}"}**
Thank you for submitting your partner application. We have received your registration for **${"${stationName}"}** and our team will review it shortly.

[Summary]
Hotel Name: ${"${stationName}"}
Address: ${"${partnerAddress}"}
Email (to receive Booking): ${"${email}"}
Submitted At: ${"${requestDateUtc}"}
[/Summary]

[Notes: Next Steps]
- Our team will review your application details shortly.
- If we need more information, we will contact you at the email address above.
[/Notes]
${DEFAULT_SIMPLE_EMAIL_FOOTER_BLOCK}`;

const stationRequestApprovalTemplate = `${SIMPLE_EMAIL_TEMPLATE_MARKER}
Eyebrow: WhaleLO Partnership
Title: Station Request Approved
Subtitle: Your partner application has been approved and your station is ready for the next step.

Hello **\${ownerName}**
Congratulations. Your partner application for **\${businessName}** has been reviewed and approved by the WhaleLO team.

[Summary]
Hotel Name: \${businessName}
Email (to receive Booking): \${email}
Password: \${password}
Partner Dashboard: \${loginUrl}
[/Summary]

[Notes: Next Steps]
- Use the partner dashboard link above to sign in and complete your onboarding.
- Use the password above for the initial login created during registration.
[/Notes]
${DEFAULT_SIMPLE_EMAIL_FOOTER_BLOCK}`;

const stationRequestDenialTemplate = `${SIMPLE_EMAIL_TEMPLATE_MARKER}
Eyebrow: WhaleLO Partnership
Title: Station Request Update
Subtitle: We have completed the review of your partner application.

Hello **\${ownerName}**
Thank you for your interest in partnering with WhaleLO. After reviewing the application for **\${businessName}**, we are unable to approve it at this time.

[Summary]
Hotel Name: \${businessName}
Email (to receive Booking): \${email}
Reason: \${reason}
[/Summary]

[Notes: Next Steps]
- Please review the reason above and update your application details if needed.
- You can contact WhaleLO support if you would like further clarification before resubmitting.
[/Notes]
${DEFAULT_SIMPLE_EMAIL_FOOTER_BLOCK}`;

const stationRequestAdminNotificationTemplate = `${SIMPLE_EMAIL_TEMPLATE_MARKER}
Eyebrow: WhaleLO Admin
Title: New Station Request
Subtitle: A new partner registration has been submitted and is waiting for admin review.

Hello **Admin**
A new station request has just been submitted to WhaleLO and needs to be reviewed from the admin dashboard.

[Summary]
Request ID: #\${formId}
Hotel Name: \${businessName}
Business Type: \${businessType}
Address: \${partnerAddress}
Email (to receive Booking): \${email}
Phone number: \${ownerPhone}
Verification Method: \${verificationMethod}
Submitted At: \${requestDateUtc}
Review URL: \${reviewUrl}
[/Summary]

[Notes: Action Required]
- Review the application details on the station requests dashboard.
- Verify the business information and contact details before making a decision.
- Approve or deny the request from the admin panel after review.
[/Notes]
${DEFAULT_SIMPLE_EMAIL_FOOTER_BLOCK}`;

/**
 * Optional layout: split the blue header into bands; middle band works well for `${verificationPin}`.
 * In each [HeaderTop] / [HeaderMiddle] / [HeaderBottom], set TitleSize / SubtitleSize (px, 8–96) before Title:/Subtitle: lines. Header text is centered by default.
 */
export const EXAMPLE_ORDER_PAID_HEADER_ZONES = `${SIMPLE_EMAIL_TEMPLATE_MARKER}
[HeaderTop]
Eyebrow: WhaleLO Booking
TitleSize: 22
Title: Booking Confirmed
Subtitle: Your reservation has been successfully confirmed.
[/HeaderTop]
[HeaderMiddle]
TitleSize: 28
SubtitleSize: 12
Title: \${verificationPin}
Subtitle: Show this code at the station
[/HeaderMiddle]
[HeaderBottom]
SubtitleSize: 11
Subtitle: Please review the booking details below.
[/HeaderBottom]

Hello **\${customerName}**
Please keep this email ready when you arrive at the station.

[Summary]
Booking Number: \${orderId}
Customer Name: \${customerName}
Customer Phone: \${customerFullPhone}
Email: \${customerEmail}
Number of Luggages: \${quantity}
Amount!: \${total} USD
Drop-off Date: \${formattedTimeIn} (LOCAL TIME)
Pick-up Date: \${formattedTimeOut} (LOCAL TIME)
Storage Location: \${partnerName}
Address: \${partnerAddress}
Google Maps: \${mapLink}
[/Summary]

[Notes: Arrival Notes]
- Present this confirmation email when you arrive at the storage reception.
- For extra luggage or longer storage duration, create a new booking for the additional service.
[/Notes]

Thank you for choosing WhaleLO. If you need support, our team will be happy to help.
Best regards,
**WhaleLO Booking Team**
${DEFAULT_SIMPLE_EMAIL_FOOTER_BLOCK}`;

// Helper: build update-flow starter từ customerPaidTemplate với Title/Subtitle custom.
const buildUpdateTemplate = (title: string, subtitle: string) =>
	customerPaidTemplate.replace(/^Title: .*$/m, `Title: ${title}`).replace(/^Subtitle: .*$/m, `Subtitle: ${subtitle}`);

// Generic update (fallback khi >1 thay đổi)
const customerUpdateTemplate = buildUpdateTemplate(
	"Booking Updated",
	"Your reservation has been updated. Please review the updated booking details below."
);
const adminUpdateTemplate = buildUpdateTemplate(
	"Booking Updated (Admin notification)",
	"A booking has been updated. Please review the updated details below."
);
const partnerUpdateTemplate = buildUpdateTemplate(
	"Booking Updated (Station notification)",
	"A reservation at your station has been updated. Please review the updated details below."
);

// Date change templates
const customerUpdateDateTemplate = buildUpdateTemplate(
	"Date change confirmed",
	"Your booking date has been updated. Please use the latest dates when you arrive at the station."
);
const adminUpdateDateTemplate = buildUpdateTemplate(
	"Date change confirmed (Admin notification)",
	"A booking date has been changed. Please review the new dates below."
);
const partnerUpdateDateTemplate = buildUpdateTemplate(
	"Date change confirmed (Station notification)",
	"A booking at your station has new dates. Please update your reservation board."
);

// Address change templates
const customerUpdateAddressTemplate = buildUpdateTemplate(
	"Address change confirmed",
	"Your booking has been moved to a new station. Please use the latest address below."
);
const adminUpdateAddressTemplate = buildUpdateTemplate(
	"Address change confirmed (Admin notification)",
	"A booking has been moved to a new station. Please review the new address below."
);
const partnerUpdateAddressTemplate = buildUpdateTemplate(
	"Address change confirmed (Station notification)",
	"A booking has been reassigned. Please verify with the customer and update your records."
);

// Settlement / Payment flow starter templates
const debtConfirmationToPartnerTemplate = `${SIMPLE_EMAIL_TEMPLATE_MARKER}
Title: WhaleLO's Debt Confirmation of \${period}
Subtitle: Please review your outstanding balance and confirm before the deadline below.

Hello **\${partnerName}**
WhaleLO would like to confirm that your outstanding balance for **\${period}** is **USD \${amount}**.

[Summary]
Period: \${period}
Total Amount: USD \${amount}
Request By: \${requestAt}
Confirm By: \${confirmAt}
[/Summary]

[Notes: Important]
- Please kindly review the attached file for detailed information. The payment will be made as scheduled.
- If you have any requests for changes, please send them by \${requestAt}.
- Confirm receipt by \${confirmAt}.
[/Notes]
${DEFAULT_SIMPLE_EMAIL_FOOTER_BLOCK}`;

const paymentProcessingToPartnerTemplate = `${SIMPLE_EMAIL_TEMPLATE_MARKER}
Title: [WhaleLO] Confirmation of Payment Processing & Booking Report (\${paymentDateUtc})
Subtitle: Your payment request has been successfully processed by our finance department.

Hello **\${stationName}**
We are delighted to inform you that your recent payment request has been successfully completed.

[Summary]
Amount Processed: \${paidAmount} \${currencyCode}
Payment Date: \${paymentDateUtc}
Time (UTC): \${paymentTimeUtc}
[/Summary]

[Notes: Attached Report]
- We have attached an Excel spreadsheet (**report.xlsx**) containing a detailed breakdown of all user bookings associated with this settlement.
- If you have any questions, please contact our support team.
[/Notes]
${DEFAULT_SIMPLE_EMAIL_FOOTER_BLOCK}`;

// Email duyệt / từ chối yêu cầu rút tiền (#33/#34) — chữ Quân chốt 03/11/2026. Hỗ trợ qua
// phiếu ở Station Portal (ac@whalelo.net đã bỏ); chân thư không có info@ / "& Delivery".
const PAYOUT_SUPPORT_TICKET_URL = "https://stations.whalelo.net/support?category=payout_issue";
const PAYOUT_EMAIL_FOOTER_BLOCK = `
[Footer]
Title: WhaleLO - Luggage Storage
Hotline: (+84) 866 888 431
Email:
Website: https://whalelo.net|whalelo.net
Note: This is an automated email. Please do not reply directly.
[/Footer]`;

const paymentApprovedToPartnerTemplate = `${SIMPLE_EMAIL_TEMPLATE_MARKER}
Eyebrow: WhaleLO Finance
Title: Payout Request Approved
Subtitle: Luggage Storage

Hello **\${displayName}**,
Your payout request has been approved.

[Summary]
Station name: \${stationName}
Approved requests: \${requestCount}
Request date: \${requestDates}
Approved amount!: \${approvedAmount} \${currencyCode}
Payout account: \${payoutAccount}
Scheduled payout period: \${payoutPeriod}
[/Summary]

[Notes: What happens next]
- These bookings are now in this payout.
- We'll pay you in the period above.
- Track it anytime in the [Station Portal](https://stations.whalelo.net).
- The booking list is attached.
[/Notes]

Questions? [Open a ticket](${PAYOUT_SUPPORT_TICKET_URL})
${PAYOUT_EMAIL_FOOTER_BLOCK}`;

const paymentRejectedToPartnerTemplate = `${SIMPLE_EMAIL_TEMPLATE_MARKER}
Eyebrow: WhaleLO Finance
Title: Payout Request Not Approved
Subtitle: Luggage Storage

Dear **\${displayName}**,
We regret to inform you that your payout request could not be approved at this time. Details are provided below.

[Summary]
Station name: \${stationName}
Request date: \${requestDate}
Requested amount: \${requestAmount} \${currencyCode}
Reason!: \${rejectReason}
[/Summary]

[Notes: Your balance has been restored]
- The funds from this request have been returned to your available balance. You may submit a new payout request at any time once the issue has been resolved.
[/Notes]

Questions? [Open a ticket](${PAYOUT_SUPPORT_TICKET_URL})
${PAYOUT_EMAIL_FOOTER_BLOCK}`;

export const getSimpleEmailStarter = (selectedId: number | null) => {
	switch (selectedId) {
		case 1:
			return customerPaidTemplate;
		case 2:
			return adminPaidTemplate;
		case 3:
			return partnerPaidTemplate;
		case 13:
			return customerUpdateTemplate;
		case 14:
			return adminUpdateTemplate;
		case 15:
			return partnerUpdateTemplate;
		case 16:
			return customerUpdateDateTemplate;
		case 17:
			return adminUpdateDateTemplate;
		case 18:
			return partnerUpdateDateTemplate;
		case 22:
			return customerUpdateAddressTemplate;
		case 23:
			return adminUpdateAddressTemplate;
		case 24:
			return partnerUpdateAddressTemplate;
		case 31:
			return debtConfirmationToPartnerTemplate;
		case 32:
			return paymentProcessingToPartnerTemplate;
		case 33:
			return paymentApprovedToPartnerTemplate;
		case 34:
			return paymentRejectedToPartnerTemplate;
		case 4:
			return customerCancelTemplate;
		case 5:
			return adminCancelTemplate;
		case 6:
			return partnerCancelTemplate;
		case 7:
			return paymentRequestConfirmationTemplate;
		case 8:
			return paymentFinalizedTemplate;
		case 9:
			return stationRequestConfirmationTemplate;
		case 10:
			return stationRequestApprovalTemplate;
		case 11:
			return stationRequestDenialTemplate;
		case 12:
			return stationRequestAdminNotificationTemplate;
		default:
			return `${SIMPLE_EMAIL_TEMPLATE_MARKER}
Eyebrow: WhaleLO Booking
Title: WhaleLO Email
Subtitle: Write your email content below.

Hello
Add your message here.

[Summary]
Label: Value
[/Summary]
${DEFAULT_SIMPLE_EMAIL_FOOTER_BLOCK}`;
	}
};
